/**
 * PriorityBadge - small colored pill for a ticket priority.
 *
 * Urgent is reserved for agents; customers never see Urgent in the
 * composer but they may see an existing Urgent ticket they're
 * watching, so we still render it here.
 */
import type { TicketPriority } from "@/types/api";

interface PriorityInfo {
  label: string;
  classes: string;
}

const PRIORITY_INFO: Record<TicketPriority, PriorityInfo> = {
  low: { label: "Low", classes: "bg-neutral-100 text-neutral-700" },
  medium: { label: "Medium", classes: "bg-blue-100 text-blue-700" },
  high: { label: "High", classes: "bg-amber-100 text-amber-700" },
  urgent: { label: "Urgent", classes: "bg-red-100 text-red-700" },
};

// Defensive fallback - same enum-drift class of crash StatusBadge hit
// after the first FD import. No broken data here today, but cheap
// insurance against any future TicketPriority addition shipping
// before the badge gets rebuilt.
const UNKNOWN_INFO: PriorityInfo = {
  label: "Unknown",
  classes: "bg-neutral-200 text-neutral-600",
};

const BASE_CLASSES =
  "inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium";

export interface PriorityBadgeProps {
  priority: TicketPriority;
  className?: string;
}

export function PriorityBadge({
  priority,
  className = "",
}: PriorityBadgeProps): JSX.Element {
  const info = PRIORITY_INFO[priority] ?? {
    ...UNKNOWN_INFO,
    label: priority ? String(priority) : UNKNOWN_INFO.label,
  };
  return (
    <span
      data-priority={priority}
      className={`${BASE_CLASSES} ${info.classes} ${className}`}
    >
      {info.label}
    </span>
  );
}

export default PriorityBadge;
